import { pageOverlayStyle, localPoint, localDelta } from '@/lib/pageGeometry';
import { useState, useRef, useCallback, useEffect } from 'react';
import { getAnnotationBBox, getGroupBBox } from '@/hooks/useAnnotations';

let annotIdCounter = 0;

/** 8-point resize handle definitions (matches EditLayer pattern). */
const ANNOT_HANDLES = [
  { id: 'tl', cursor: 'nwse-resize', cx: -1, cy: -1 },
  { id: 'tc', cursor: 'ns-resize',   cx:  0, cy: -1 },
  { id: 'tr', cursor: 'nesw-resize', cx:  1, cy: -1 },
  { id: 'lc', cursor: 'ew-resize',   cx: -1, cy:  0 },
  { id: 'rc', cursor: 'ew-resize',   cx:  1, cy:  0 },
  { id: 'bl', cursor: 'nesw-resize', cx: -1, cy:  1 },
  { id: 'bc', cursor: 'ns-resize',   cx:  0, cy:  1 },
  { id: 'br', cursor: 'nwse-resize', cx:  1, cy:  1 },
];

/** Move an annotation by (dx, dy) in page coordinates. Returns a new object. */
function moveAnnot(a, dx, dy) {
  if (a.type === 'draw') {
    return { ...a, points: a.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
  }
  if (a.type === 'line' || a.type === 'arrow') {
    return { ...a, x1: a.x1 + dx, y1: a.y1 + dy, x2: a.x2 + dx, y2: a.y2 + dy };
  }
  // rect-based + text
  return { ...a, x: a.x + dx, y: a.y + dy };
}

/** Resize an annotation from oldBBox to newBBox. Returns a new object. */
function resizeAnnot(a, oldBBox, newBBox) {
  if (oldBBox.width === 0 || oldBBox.height === 0) return a;
  const sx = newBBox.width / oldBBox.width;
  const sy = newBBox.height / oldBBox.height;

  if (a.type === 'draw') {
    return {
      ...a,
      points: a.points.map((p) => ({
        x: newBBox.x + (p.x - oldBBox.x) * sx,
        y: newBBox.y + (p.y - oldBBox.y) * sy,
      })),
    };
  }
  if (a.type === 'line' || a.type === 'arrow') {
    return {
      ...a,
      x1: newBBox.x + (a.x1 - oldBBox.x) * sx,
      y1: newBBox.y + (a.y1 - oldBBox.y) * sy,
      x2: newBBox.x + (a.x2 - oldBBox.x) * sx,
      y2: newBBox.y + (a.y2 - oldBBox.y) * sy,
    };
  }
  if (a.type === 'text') {
    // Text just moves — font size stays the same
    return { ...a, x: newBBox.x, y: newBBox.y };
  }
  // rect-based: highlight, underline, strikethrough, rectangle, ellipse, checkmark, cross
  return { ...a, x: newBBox.x, y: newBBox.y, w: newBBox.width, h: newBBox.height };
}

/**
 * AnnotationLayer — SVG overlay for a single PDF page.
 *
 * Renders existing annotations and handles mouse interactions for creating
 * new ones. Coordinates are in page space (top-left origin, scale=1).
 * The SVG viewBox maps directly to page dimensions so all rendering is
 * zoom-independent — the SVG is just scaled by CSS width/height.
 */
export default function AnnotationLayer({
  pageNum,
  geometry,
  pageWidth,
  pageHeight,
  zoom,
  annotations,
  activeTool,
  color,
  opacity,
  strokeWidth,
  fontSize,
  onAddAnnotation,
  // Selection props
  selectable,
  selectedGroupId,
  onSelectGroup,
  onDeselectGroup,
  onDeleteAnnotation,
  onDeleteGroup,
  onReplaceGroupAnnotations,
  onCommitAnnotationUpdate,
}) {
  const svgRef = useRef(null);
  const [drawing, setDrawing] = useState(null); // in-progress annotation
  const [editingText, setEditingText] = useState(null); // { id, x, y }
  const [dragState, setDragState] = useState(null);
  const [resizeState, setResizeState] = useState(null);

  // Convert mouse event to page coordinates
  const toPageCoords = useCallback(
    (e) => {
      const rect = svgRef.current.getBoundingClientRect();
      return localPoint(e, rect, zoom, pageWidth, pageHeight, geometry?.rotation);
    },
    [pageWidth, pageHeight, zoom, geometry]
  );

  const handleMouseDown = useCallback(
    (e) => {
      // Clear ALL selection when clicking empty space (annotations + edits)
      if (onDeselectGroup) onDeselectGroup();
      if (!activeTool || activeTool === 'eraser') return;
      e.preventDefault();
      const { x, y } = toPageCoords(e);
      const annotType = activeTool === 'highlight-draw' ? 'draw' : activeTool;

      if (annotType === 'draw') {
        setDrawing({ type: 'draw', points: [{ x, y }], color, opacity, strokeWidth });
      } else if (['highlight', 'underline', 'strikethrough'].includes(annotType)) {
        setDrawing({ type: annotType, x, y, w: 0, h: 0, color, opacity });
      } else if (['rectangle', 'ellipse', 'checkmark', 'cross'].includes(annotType)) {
        setDrawing({ type: annotType, x, y, w: 0, h: 0, color, opacity, strokeWidth });
      } else if (annotType === 'line' || annotType === 'arrow') {
        setDrawing({ type: annotType, x1: x, y1: y, x2: x, y2: y, color, opacity, strokeWidth });
      }
    },
    [activeTool, color, opacity, strokeWidth, toPageCoords, onDeselectGroup]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!drawing) return;
      const { x, y } = toPageCoords(e);

      setDrawing((prev) => {
        if (!prev) return null;
        if (prev.type === 'draw') {
          return { ...prev, points: [...prev.points, { x, y }] };
        } else if (prev.type === 'line' || prev.type === 'arrow') {
          return { ...prev, x2: x, y2: y };
        } else {
          return { ...prev, w: x - prev.x, h: y - prev.y };
        }
      });
    },
    [drawing, toPageCoords]
  );

  const handleMouseUp = useCallback(() => {
    if (!drawing) return;

    // Only add if the annotation has meaningful size
    const id = `ann-${++annotIdCounter}`;
    let annotation = null;

    if (drawing.type === 'draw' && drawing.points.length > 2) {
      annotation = { id, pageNum, ...drawing };
    } else if (drawing.type === 'line' || drawing.type === 'arrow') {
      const dx = Math.abs(drawing.x2 - drawing.x1);
      const dy = Math.abs(drawing.y2 - drawing.y1);
      if (dx > 3 || dy > 3) annotation = { id, pageNum, ...drawing };
    } else if (drawing.w !== undefined) {
      // Normalize negative width/height (dragged left/up)
      let { x, y, w, h, ...rest } = drawing;
      if (w < 0) { x += w; w = -w; }
      if (h < 0) { y += h; h = -h; }
      if (w > 3 || h > 3) annotation = { id, pageNum, x, y, w, h, ...rest };
    }

    if (annotation) onAddAnnotation(annotation);
    setDrawing(null);
  }, [drawing, pageNum, onAddAnnotation]);

  const handleClick = useCallback(
    (e) => {
      if (activeTool === 'eraser') {
        // Find annotation at click position
        const { x, y } = toPageCoords(e);
        const hit = [...annotations].reverse().find((a) => hitTest(a, x, y));
        if (hit) onDeleteAnnotation(hit.id);
      }
    },
    [activeTool, annotations, toPageCoords, onDeleteAnnotation]
  );

  // --- Selection + drag ---
  const handleAnnotMouseDown = useCallback(
    (groupId, groupAnnots, e) => {
      e.stopPropagation();
      e.preventDefault();

      // Select the group
      if (selectedGroupId !== groupId && onSelectGroup) {
        onSelectGroup(groupId);
      }

      // Set up potential drag (threshold-based, like EditLayer)
      const startX = e.clientX;
      const startY = e.clientY;
      const DRAG_THRESHOLD = 3;

      const onMove = (me) => {
        if (Math.abs(me.clientX - startX) > DRAG_THRESHOLD || Math.abs(me.clientY - startY) > DRAG_THRESHOLD) {
          cleanup();
          setDragState({
            groupId,
            startMouseX: startX,
            startMouseY: startY,
            originalAnnots: groupAnnots,
          });
        }
      };
      const onUp = () => cleanup();
      const cleanup = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [selectedGroupId, onSelectGroup]
  );

  const handleResizeStart = useCallback(
    (handle, groupAnnots, groupBBox, e) => {
      e.stopPropagation();
      e.preventDefault();
      setResizeState({
        handle,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startBBox: groupBBox,
        groupId: selectedGroupId,
        originalAnnots: groupAnnots,
      });
    },
    [selectedGroupId]
  );

  const handleTextConfirm = useCallback(() => {
    if (!editingText || !editingText.text.trim()) {
      setEditingText(null);
      return;
    }
    onAddAnnotation({
      id: `ann-${++annotIdCounter}`,
      pageNum,
      type: 'text',
      x: editingText.x,
      y: editingText.y,
      text: editingText.text.trim(),
      color,
      opacity: 1,
      fontSize,
    });
    setEditingText(null);
  }, [editingText, pageNum, color, fontSize, onAddAnnotation]);

  // Escape key cancels active drawing/text
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setDrawing(null);
        if (editingText) handleTextConfirm();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [editingText, handleTextConfirm]);

  // Drag-to-move effect
  useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (e) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const scaleX = 1 / zoom;
      const scaleY = 1 / zoom;
      const { x: dx, y: dy } = localDelta((e.clientX - dragState.startMouseX) * scaleX, (e.clientY - dragState.startMouseY) * scaleY, geometry?.rotation);
      if (onReplaceGroupAnnotations) {
        onReplaceGroupAnnotations(dragState.groupId, (a) => {
          const original = dragState.originalAnnots.find((o) => o.id === a.id);
          return original ? moveAnnot(original, dx, dy) : a;
        });
      }
    };

    const handleMouseUp = () => {
      setDragState(null);
      if (onCommitAnnotationUpdate) onCommitAnnotationUpdate();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, pageWidth, pageHeight, geometry, zoom, onReplaceGroupAnnotations, onCommitAnnotationUpdate]);

  // Resize effect
  useEffect(() => {
    if (!resizeState) return;

    const handleMouseMove = (e) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const scaleX = 1 / zoom;
      const scaleY = 1 / zoom;
      const { x: dx, y: dy } = localDelta((e.clientX - resizeState.startMouseX) * scaleX, (e.clientY - resizeState.startMouseY) * scaleY, geometry?.rotation);
      const h = resizeState.handle;
      let { x, y, width, height } = resizeState.startBBox;

      if (h.cx === -1) { x += dx; width -= dx; }
      else if (h.cx === 1) { width += dx; }
      if (h.cy === -1) { y += dy; height -= dy; }
      else if (h.cy === 1) { height += dy; }

      const minDim = 10;
      if (width < minDim) { width = minDim; if (h.cx === -1) x = resizeState.startBBox.x + resizeState.startBBox.width - minDim; }
      if (height < minDim) { height = minDim; if (h.cy === -1) y = resizeState.startBBox.y + resizeState.startBBox.height - minDim; }

      if (x < 0) { width += x; x = 0; }
      if (y < 0) { height += y; y = 0; }
      if (x + width > pageWidth) width = pageWidth - x;
      if (y + height > pageHeight) height = pageHeight - y;

      const newBBox = { x, y, width, height };
      if (onReplaceGroupAnnotations) {
        onReplaceGroupAnnotations(resizeState.groupId, (a) => {
          const original = resizeState.originalAnnots.find((o) => o.id === a.id);
          return original ? resizeAnnot(original, resizeState.startBBox, newBBox) : a;
        });
      }
    };

    const handleMouseUp = () => {
      setResizeState(null);
      if (onCommitAnnotationUpdate) onCommitAnnotationUpdate();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizeState, pageWidth, pageHeight, geometry, zoom, onReplaceGroupAnnotations, onCommitAnnotationUpdate]);

  // Tombstoned (deleted: true) annotations stay in state so the save pipeline
  // can whiteout their baked pixels on next save — but they must not render.
  const pageAnnotations = annotations.filter((a) => a.pageNum === pageNum && !a.deleted);

  // Build group map: groupId → [annotations]
  const groupMap = new Map();
  for (const a of pageAnnotations) {
    const gid = a.groupId || a.id;
    if (!groupMap.has(gid)) groupMap.set(gid, []);
    groupMap.get(gid).push(a);
  }

  const selectedGroupAnnots = selectedGroupId ? (groupMap.get(selectedGroupId) || []) : [];
  const selectedBBox = selectedGroupAnnots.length > 0 ? getGroupBBox(selectedGroupAnnots) : null;
  const hasTextOnly = selectedGroupAnnots.length === 1 && selectedGroupAnnots[0].type === 'text';
  const cursorClass = activeTool === 'eraser' ? 'cursor-pointer' : activeTool ? 'cursor-crosshair' : 'cursor-default';

  return (
    <div className="absolute inset-0" style={{ ...pageOverlayStyle(geometry, zoom), width: `${pageWidth * zoom}px`, height: `${pageHeight * zoom}px`, pointerEvents: 'none' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${pageWidth} ${pageHeight}`}
        className={`absolute inset-0 w-full h-full ${cursorClass}`}
        style={{ pointerEvents: (activeTool || (selectable && pageAnnotations.length > 0)) ? 'auto' : 'none' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleClick}
      >
        {/* Existing annotations */}
        {pageAnnotations.map((a) => (
          <AnnotationShape key={a.id} annotation={a} />
        ))}

        {/* Selection highlight (dashed rect around selected group) */}
        {selectedBBox && (
          <rect
            x={selectedBBox.x} y={selectedBBox.y}
            width={selectedBBox.width} height={selectedBBox.height}
            fill="none" stroke="#4F46E5" strokeWidth={1.5 / zoom}
            strokeDasharray={`${4 / zoom} ${3 / zoom}`}
            pointerEvents="none"
          />
        )}

        {/* In-progress drawing */}
        {drawing && <AnnotationShape annotation={drawing} />}

        {/* Invisible hit targets — one per GROUP, only when no drawing tool active */}
        {selectable && !activeTool && Array.from(groupMap.entries()).map(([gid, groupAnnots]) => {
          const bbox = getGroupBBox(groupAnnots);
          if (!bbox) return null;
          const margin = 4;
          return (
            <rect
              key={`hit-${gid}`}
              x={bbox.x - margin} y={bbox.y - margin}
              width={bbox.width + margin * 2} height={bbox.height + margin * 2}
              fill="transparent" pointerEvents="all"
              style={{ cursor: selectedGroupId === gid ? 'move' : 'pointer' }}
              onMouseDown={(e) => handleAnnotMouseDown(gid, groupAnnots, e)}
            />
          );
        })}
      </svg>

      {/* Selection handles + delete button (HTML overlay) */}
      {selectedBBox && !dragState && !resizeState && selectable && (
        <div
          className="absolute"
          style={{
            left: `${selectedBBox.x * zoom}px`,
            top: `${selectedBBox.y * zoom}px`,
            width: `${selectedBBox.width * zoom}px`,
            height: `${selectedBBox.height * zoom}px`,
            pointerEvents: 'none',
          }}
        >
          {/* Resize handles */}
          {!hasTextOnly && ANNOT_HANDLES.map((h) => (
            <div
              key={h.id}
              onMouseDown={(e) => handleResizeStart(h, selectedGroupAnnots, selectedBBox, e)}
              className="bg-brand-blue border-2 border-white shadow-sm rounded-sm"
              style={{
                position: 'absolute',
                width: '8px',
                height: '8px',
                left: `calc(${(h.cx + 1) / 2 * 100}% - 4px)`,
                top: `calc(${(h.cy + 1) / 2 * 100}% - 4px)`,
                cursor: h.cursor,
                pointerEvents: 'auto',
                zIndex: 10,
              }}
            />
          ))}
          {/* Delete button */}
          <button
            onClick={(e) => { e.stopPropagation(); if (onDeleteGroup) onDeleteGroup(selectedGroupId); }}
            className="absolute -top-2.5 -right-2.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-600 z-10"
            style={{ pointerEvents: 'auto' }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Text editing input (HTML overlay, not SVG) */}
      {editingText && (
        <textarea
          autoFocus
          value={editingText.text}
          onChange={(e) => setEditingText((prev) => ({ ...prev, text: e.target.value }))}
          onBlur={handleTextConfirm}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleTextConfirm(); }
          }}
          className="absolute border border-brand-blue rounded px-1 py-0.5 text-sm bg-white/90 outline-none resize-none shadow-sm"
          style={{
            left: `${editingText.x * zoom}px`,
            top: `${editingText.y * zoom}px`,
            minWidth: '80px',
            minHeight: '28px',
            fontSize: `${fontSize * zoom}px`,
            color,
            lineHeight: 1.3,
            pointerEvents: 'auto',
          }}
        />
      )}
    </div>
  );
}

// ==================== Render individual annotation shapes ====================

function AnnotationShape({ annotation: a }) {
  if (a.type === 'highlight') {
    return (
      <rect
        x={a.x} y={a.y} width={Math.abs(a.w)} height={Math.abs(a.h)}
        fill={a.color} opacity={a.opacity} rx={1}
      />
    );
  }

  if (a.type === 'underline') {
    const y = a.y + Math.abs(a.h);
    return <line x1={a.x} y1={y} x2={a.x + Math.abs(a.w)} y2={y} stroke={a.color} strokeWidth={1.5} opacity={a.opacity} />;
  }

  if (a.type === 'strikethrough') {
    const y = a.y + Math.abs(a.h) / 2;
    return <line x1={a.x} y1={y} x2={a.x + Math.abs(a.w)} y2={y} stroke={a.color} strokeWidth={1.5} opacity={a.opacity} />;
  }

  if (a.type === 'draw' && a.points?.length > 1) {
    const d = pointsToPath(a.points);
    return (
      <path d={d} fill="none" stroke={a.color} strokeWidth={a.strokeWidth} strokeLinecap="round" strokeLinejoin="round" opacity={a.opacity} />
    );
  }

  if (a.type === 'text') {
    return (
      <g>
        <rect
          x={a.x - 2} y={a.y - 2}
          width={a.text.length * a.fontSize * 0.6 + 8}
          height={a.fontSize * 1.4 + 4}
          fill="white" fillOpacity={0.85} rx={2}
        />
        <text x={a.x + 2} y={a.y + a.fontSize} fill={a.color} fontSize={a.fontSize} fontFamily="sans-serif">
          {a.text}
        </text>
      </g>
    );
  }

  if (a.type === 'rectangle') {
    return (
      <rect
        x={Math.min(a.x, a.x + a.w)} y={Math.min(a.y, a.y + a.h)}
        width={Math.abs(a.w)} height={Math.abs(a.h)}
        fill="none" stroke={a.color} strokeWidth={a.strokeWidth} opacity={a.opacity} rx={1}
      />
    );
  }

  if (a.type === 'ellipse') {
    const cx = a.x + a.w / 2;
    const cy = a.y + a.h / 2;
    return (
      <ellipse
        cx={cx} cy={cy} rx={Math.abs(a.w / 2)} ry={Math.abs(a.h / 2)}
        fill="none" stroke={a.color} strokeWidth={a.strokeWidth} opacity={a.opacity}
      />
    );
  }

  if (a.type === 'line') {
    return (
      <line
        x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2}
        stroke={a.color} strokeWidth={a.strokeWidth} opacity={a.opacity} strokeLinecap="round"
      />
    );
  }

  if (a.type === 'checkmark') {
    const w = Math.abs(a.w), h = Math.abs(a.h);
    const nx = Math.min(a.x, a.x + a.w);
    const ny = Math.min(a.y, a.y + a.h);
    return (
      <polyline
        points={`${nx + w * 0.15},${ny + h * 0.55} ${nx + w * 0.4},${ny + h * 0.8} ${nx + w * 0.85},${ny + h * 0.2}`}
        fill="none"
        stroke={a.color}
        strokeWidth={a.strokeWidth || 3}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={a.opacity ?? 1}
      />
    );
  }

  if (a.type === 'cross') {
    const nx = Math.min(a.x, a.x + a.w);
    const ny = Math.min(a.y, a.y + a.h);
    const w = Math.abs(a.w), h = Math.abs(a.h);
    const pad = Math.min(w, h) * 0.15;
    return (
      <g opacity={a.opacity ?? 1}>
        <line x1={nx + pad} y1={ny + pad} x2={nx + w - pad} y2={ny + h - pad}
          stroke={a.color} strokeWidth={a.strokeWidth || 3} strokeLinecap="round" />
        <line x1={nx + w - pad} y1={ny + pad} x2={nx + pad} y2={ny + h - pad}
          stroke={a.color} strokeWidth={a.strokeWidth || 3} strokeLinecap="round" />
      </g>
    );
  }

  if (a.type === 'arrow') {
    const dx = a.x2 - a.x1, dy = a.y2 - a.y1;
    const angle = Math.atan2(dy, dx);
    const headLen = Math.min(15, Math.sqrt(dx * dx + dy * dy) * 0.3);
    const x3 = a.x2 - headLen * Math.cos(angle - Math.PI / 6);
    const y3 = a.y2 - headLen * Math.sin(angle - Math.PI / 6);
    const x4 = a.x2 - headLen * Math.cos(angle + Math.PI / 6);
    const y4 = a.y2 - headLen * Math.sin(angle + Math.PI / 6);
    return (
      <g opacity={a.opacity ?? 1}>
        <line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2}
          stroke={a.color} strokeWidth={a.strokeWidth || 2} strokeLinecap="round" />
        <polyline points={`${x3},${y3} ${a.x2},${a.y2} ${x4},${y4}`}
          fill="none" stroke={a.color} strokeWidth={a.strokeWidth || 2} strokeLinecap="round" strokeLinejoin="round" />
      </g>
    );
  }

  return null;
}

// ==================== Helpers ====================

function pointsToPath(points) {
  if (points.length < 2) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const mid = {
      x: (points[i].x + points[i + 1].x) / 2,
      y: (points[i].y + points[i + 1].y) / 2,
    };
    d += ` Q ${points[i].x} ${points[i].y} ${mid.x} ${mid.y}`;
  }
  const last = points[points.length - 1];
  d += ` L ${last.x} ${last.y}`;
  return d;
}

function hitTest(a, x, y, margin = 8) {
  if (a.type === 'draw') {
    return a.points?.some((p) => Math.abs(p.x - x) < margin && Math.abs(p.y - y) < margin);
  }
  if (a.type === 'line' || a.type === 'arrow') {
    return distToSegment(x, y, a.x1, a.y1, a.x2, a.y2) < margin;
  }
  if (a.type === 'text') {
    const w = a.text.length * a.fontSize * 0.6 + 8;
    const h = a.fontSize * 1.4 + 4;
    return x >= a.x - margin && x <= a.x + w + margin && y >= a.y - margin && y <= a.y + h + margin;
  }
  // Rect-based (highlight, underline, strikethrough, rectangle, ellipse)
  if (a.x !== undefined && a.w !== undefined) {
    const ax = Math.min(a.x, a.x + a.w);
    const ay = Math.min(a.y, a.y + a.h);
    const aw = Math.abs(a.w);
    const ah = Math.abs(a.h);
    return x >= ax - margin && x <= ax + aw + margin && y >= ay - margin && y <= ay + ah + margin;
  }
  return false;
}

function distToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}
